import { Contract } from '../booking';
import { Destination } from '../destination';
import { EnumTypeProduct } from '../distribution/enums/enum-type-product.model';
import { IAirport } from '../flight/airport.model';
import { Formality } from '../formality';
import { ProductDescription } from './product-description.model';
import { ProductTranslation } from './product-translation.model';

/** Migration of a product to a trip of the new back-office */
export interface ProductMigration {
  status: 'IMPORTED_DRAFT' | 'MIGRATED';
  tripId: string | null;
  // Every trip holding the product, tripId first: one per set of distributors of its contracts
  tripIds?: string[];
}

/** The trips of a migrated product, the first one first; an older answer names one only. */
export function productMigrationTripIds(
  migration: ProductMigration | null | undefined
): string[] {
  if (!migration) {
    return [];
  }
  if (migration.tripIds?.length) {
    return migration.tripIds;
  }
  return migration.tripId ? [migration.tripId] : [];
}

/** The one label of a migration state, on the list as on the sheet. */
export function productMigrationLabel(migration: ProductMigration): string {
  return migration.status === 'MIGRATED' ? 'Migré vers Trip' : 'Importé dans Trip';
}

/** A legacy prestation (option, insurance) taken over by a trip with one of its products. */
export interface PrestationLock {
  productId: number | null;
  tripId: string | null;
  // The prestation that took over in the new back-office, when the import found one
  prestationId?: string | null;
  // The lock could not be read: kept read-only, as the API refuses changes until it can tell
  unverified?: boolean;
}

export const UNVERIFIED_LOCK: PrestationLock = {
  productId: null,
  tripId: null,
  unverified: true,
};

/** A page of the new back-office, null without a path or a configured url. */
export function newBackOfficePageUrl(
  newBackOfficeUrl: string | undefined,
  lang: string,
  path: string | null | undefined
): string | null {
  if (!path || !newBackOfficeUrl) {
    return null;
  }
  return `${newBackOfficeUrl}/${encodeURIComponent(lang || 'fr')}/${path}`;
}

/** The trip preview in the new back-office, null without a trip or a configured url. */
export function tripPreviewUrl(
  newBackOfficeUrl: string | undefined,
  lang: string,
  tripId: string | null | undefined
): string | null {
  return newBackOfficePageUrl(
    newBackOfficeUrl,
    lang,
    tripId ? `products/${encodeURIComponent(tripId)}/preview` : null
  );
}

/** What a migrated product means for whoever opens it. */
export function productMigrationHint(migration: ProductMigration): string {
  return migration.status === 'MIGRATED'
    ? 'Produit migré : il se modifie dans le voyage du nouveau back-office'
    : 'Produit importé en voyage brouillon : il se modifie dans le voyage du nouveau back-office';
}

export class Product {
  id!: number;
  name!: string;
  reference!: string;
  language?: string;
  startPoint?: string;
  secondDestinationId?: number;
  catchPhrase?: string;
  subtitle?: string;
  shortDescription?: string;
  priceInDescription?: string;
  priceOffDescription?: string;
  noFlightPriceInDescription?: string;
  noFlightPriceOffDescription?: string;
  goodToKnow?: string;
  formality?: Formality;
  conditions?: string;
  transportDescription?: string;
  type?: EnumTypeProduct;
  bestDeal?: boolean;
  nbPicture?: number;
  active?: boolean;
  destination?: Destination;
  companyId?: string;
  contracts?: Contract[];
  productDescriptions?: ProductDescription[];
  productTranslations?: ProductTranslation[];
  orchestraContent?: boolean;
  // Set once the product is imported into a trip: the sheet is then read-only
  migration?: ProductMigration | null;
  // Legacy numeric id of the airport, not the flight service ObjectId (see IAirport.legacyId)
  airportId?: number | null;
  airport?: IAirport;
  nbContractWithStock?: number;
  nbContract?: number;
  nbProductRooms?: number;
  gpOrder?: string;
  nbOption?: number;
  nbFlight?: number;

  landOnly?: boolean;
}
